import {
  CreditSummaryStyles,
  WelcomeStyles
} from '@com-delta-idp/mnx-fast-action-banner-ui';
import { CSSProperties, Link } from '@com-delta-idp/mnx-ui-kit';
import { AnalyticsEvent } from '@com-delta-idp/mnx-utils';
import { LevelType } from '../tier.types';
import { AEMWelcomeTour } from './aem-welcomeTour.interfaces';

/* eslint-disable max-lines */
export interface Actions {
  next: string;
  previous: string;
}

export interface Icon {
  alt: string;
  src: string;
}

export interface Goody {
  icon: Icon;
  name: string;
  link?: Link;
}

export interface AEMTier {
  bannerLabel: string;
  current: string;
  goodies: Goody[];
  medallionId: string;
  memberStatus: string;
  peekBarLabel: string;
  title: string;
}

export interface AEMBenefits {
  actions: Actions;
  tiers: AEMTier[];
  title: string;
}

export interface AEMEarns {
  fastActionBanner: AEMFastActionBanner;
  offers: string;
  partners: string;
  title: string;
}

export interface AEMFastActionBanner {
  offerConfigs: AEMBannerConfig;
  offerStyles: AEMBannerStyles;
}

export interface AEMBannerStyles {
  annualFee?: CSSProperties;
  benefit?: CSSProperties;
  bullets?: CSSProperties;
  buttonLabel?: CSSProperties;
  checkLabel?: CSSProperties;
  checkSectionBackgroundColor?: string;
  containerStyles?: CSSProperties;
  details?: CSSProperties;
  heading1?: CSSProperties;
  heading2?: CSSProperties;
  image?: CSSProperties;
  linksStyles?: CSSProperties;
  welcome: WelcomeStyles;
  creditSummary: CreditSummaryStyles;
}

export interface AEMBannerConfig {
  congratulation?: string;
  showAction?: string;
  showBenefit?: string;
  showCreditSummary?: string;
  showDetails?: string;
  showHeadline?: string;
  showLinks?: string;
  showOffer?: string;
}

export interface MedallionAriaLabel {
  skyPriorityLabel: string;
  millionMiler: string;
  level: string;
}

export interface AEMMedallionBanner {
  statusLabel: string;
  medallionBenefitMessage: string;
  priority: Link;
  trademark: string;
  analyticsEvent: AnalyticsEvent;
  ariaLabel: MedallionAriaLabel;
}

export interface NoMilesLabel {
  label: string;
  summaryLink: string;
  id: string;
}

export interface WithMilesLabel {
  label: string;
  summaryLink: string;
  id: string;
}

export interface AEMAction {
  noMilesLabel: NoMilesLabel;
  withMilesLabel: WithMilesLabel;
}

export interface LegendsList {
  title: string;
}

export interface Legends {
  legendsList: LegendsList[];
}

export interface TrackerDetails {
  action: Link;
  completeLabel: string;
  completeOneLabel: string;
  detailsCards: string;
  dollars: string;
  flightQualifierLabel: string;
  info: string;
  tooltipInfo: string;
  legends: Legends;
  trademark: string;
  miles: string;
  pendingInfoLabel: string;
  earnedStatusLabel: string;
  delta360Label: string;
  pendingLabel: string;
  rolloverMQMLabelEndOfYear: string;
  rolloverMQMLabelBeginningOfyear: string;
  segments: string;
  spendQualifierLabel: string;
  statusProgressLabel: string;
  toLabel: string;
  knowMoreLabel: string;
  knowMoreUrl: string;
  cardImageUrl: string;
  cardImageLabel: string;
  ariaLabel: TrackerAriaLabel;
  waiverCardLabel: string;
  waiverCardToolTip: string;
  waiverInfoLabel: string;
  waiverLabelTitle: string;
}

export interface TrackerAriaLabel {
  statusLabel: string;
  currentValueLabel: string;
  minValueLabel: string;
  maxValueLabel: string;
  accureMoreLabel: string;
  svgIconLabel: string;
  andIconLabel: string;
  orIconLabel: string;
  flightIconLabel: string;
  spendIconLabel: string;
  completeIconLabel: string;
}

export interface AEMLevelStatusAriaLabel {
  millionMiler: string;
  level: string;
}

export interface TrackerStatus {
  action: Link;
  lifeTimeLabel: string;
  minMillerStatusLimit: string;
  maxMillerStatusLimit: string;
  millerStatusLabel: string;
  ariaLabel: AEMLevelStatusAriaLabel;
}

export interface AEMTracker {
  trackerDetails: TrackerDetails;
  trackerStatus: TrackerStatus;
}

export interface AEMSummary {
  action: AEMAction;
  availableMilesLabel: string;
  description: string;
  title: string;
}

export interface AEMOverview {
  summary: AEMSummary;
  tracker: AEMTracker;
}

export interface AEMImage {
  alt?: string;
  src?: string;
  label?: string;
  url?: string;
}

export interface ActionType {
  link: Link;
  type: string;
}

export interface AEMCard {
  id?: string;
  image: AEMImage;
  title: string;
  actionType?: ActionType;
}

export interface AEMMembershipCard {
  image: AEMImage;
  validThruLabel: string;
  altBarcodeText: string;
}

export interface Status {
  cards: AEMCard[];
  medallionId: string;
  medallionLabel: string;
  membershipcard: AEMMembershipCard;
}

export interface AEMSkyClub {
  executive: string; // "Executive Membership",
  expiry: string; // "Current Term Expires",
  individual: string; // "Individual Membership"
  nonMember: string;
}

export interface AEMQuickLinks {
  action: ActionType;
  info: string;
  rules: string;
  status: Status[];
  title: string;
  barcodeUrl: string;
  skyclub: AEMSkyClub;
}

export interface AEMRelatedLinkImage {
  alt: string;
  src: string;
}

export interface AEMRelatedLink {
  id: string;
  image: AEMRelatedLinkImage;
  label: string;
  url: string;
}

export interface AEMRelatedLinks {
  relatedLinkList: AEMRelatedLink[];
}

export interface AEMStipulation {
  title: string;
  description: string;
}

export interface ColumnHeader {
  title: string;
}

export interface AEMSnapshot {
  link: Link;
  subtitle: string;
  title: string;
  milesLabel: string;
  MQDLabel: string;
}

export interface Item {
  image: AEMImage;
  redeemLink: Link;
  title: string;
  type: LevelType;
}

export interface AEMUpgrades {
  items: Item[];
  link: Link;
  title: string;
}

export interface AEMCampaignId {
  banner: 'ng-skymiles:amex-banners';
  format: 'json';
  micromoments: 'ng-skymiles:micro-moments-v1';
  offers: 'ng-skymiles:promotional-cards';
  partners: 'ng-skymiles:partners';
}

export interface AEMRequestProperty {
  carrierCode: string;
  preferenceCategoryCode: string;
}

export interface Categories {
  code: string;
  name: string;
  type?: string;
  description?: string;
  tooltip?: string;
  logoSrc: string;
  logoAlt: string;
}

export interface AEMMqdSummary {
  title: string;
  linkLabel: string;
  linkUrl: string;
  linkId: string;
  categories: Categories[];
  note: string;
  noteToolTipInfo: string;
  noteLabel: string;
}

export interface AEMContent {
  benefits: AEMBenefits;
  earns: AEMEarns;
  medallionBanner: AEMMedallionBanner;
  overview: AEMOverview;
  quickLinks: AEMQuickLinks;
  relatedLink: AEMRelatedLinks;
  snapshot: AEMSnapshot;
  upgrades: AEMUpgrades;
  tour: AEMWelcomeTour;
  campaignId: AEMCampaignId;
  requestProperty: AEMRequestProperty;
  footer: AEMStipulation;
  mqdSummary: AEMMqdSummary;
}
